import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Image, Upload, Trash2, Copy, ArrowLeft, Search, 
  Music, FileImage, FolderOpen, Check, X, Filter
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { adminFetch, isAdminAuthenticated } from '@/utils/adminAuth';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full URL for media files
const getFullUrl = (url) => {
  if (!url) return '';
  // If it's already an absolute URL, return as is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // Otherwise, prepend the backend URL
  return `${BACKEND_URL}${url}`;
};

export const AdminMedia = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [setCoverFor, setSetCoverFor] = useState(null); // For setting album cover
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAdminAuthenticated()) {
      navigate('/admin');
      return;
    }
    loadMedia();
  }, []);

  const loadMedia = async () => {
    try {
      const response = await adminFetch('/admin/media');
      const data = await response.json();
      setMedia(data);
    } catch (error) {
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    let successCount = 0;

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await adminFetch('/admin/media/upload', {
          method: 'POST',
          body: formData
        });
        
        if (response.ok) {
          successCount++;
        }
      } catch (error) {
        console.error('Upload error:', error);
      }
    }

    if (successCount > 0) {
      toast.success(`Uploaded ${successCount} file(s)`);
      loadMedia();
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleDelete = async (mediaId) => {
    try {
      const response = await adminFetch(`/admin/media/${mediaId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        toast.success('File deleted');
        setMedia(media.filter(m => m.id !== mediaId));
        setDeleteConfirm(null);
      } else {
        toast.error(`Failed to delete: ${response.status}`);
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete file');
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(getFullUrl(url));
    toast.success('URL copied to clipboard');
  };

  const handleSetCover = async (audioItem, coverUrl) => {
    try {
      const response = await adminFetch(`/admin/media/${audioItem.id}/cover`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cover_url: coverUrl })
      });
      
      if (response.ok) {
        toast.success('Cover image set!');
        // Update local state
        setMedia(media.map(m => 
          m.id === audioItem.id ? { ...m, cover_url: coverUrl } : m
        ));
        setSetCoverFor(null);
      }
    } catch (error) {
      toast.error('Failed to set cover');
    }
  };

  const getFileIcon = (type) => {
    if (type?.startsWith('image')) return <FileImage className="w-5 h-5" />;
    if (type?.startsWith('audio')) return <Music className="w-5 h-5" />;
    if (type?.includes('zip') || type?.includes('archive')) return <FolderOpen className="w-5 h-5" />;
    return <FolderOpen className="w-5 h-5" />;
  };

  const getFileType = (mimeType) => {
    if (mimeType?.startsWith('image')) return 'image';
    if (mimeType?.startsWith('audio')) return 'audio';
    if (mimeType?.startsWith('video')) return 'video';
    if (mimeType?.includes('zip') || mimeType?.includes('archive')) return 'archive';
    return 'other';
  };

  const filteredMedia = media.filter(item => {
    const matchesSearch = item.filename?.toLowerCase().includes(searchQuery.toLowerCase());
    const itemType = getFileType(item.mime_type);
    const matchesType = filterType === 'all' || itemType === filterType;
    return matchesSearch && matchesType;
  });

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background noise-texture py-8 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="p-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-4xl font-bold font-display text-foreground">
                Media Library
              </h1>
              <p className="text-muted-foreground mt-1">
                {media.length} files • Upload and manage your media
              </p>
            </div>
          </div>
          
          <label className="flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold cursor-pointer transition-all hover:scale-105">
            <Upload className="w-5 h-5" />
            {uploading ? 'Uploading...' : 'Upload Files'}
            <input
              type="file"
              multiple
              accept="image/*,audio/*,video/*,.zip,.rar,.7z,application/zip,application/x-zip-compressed"
              onChange={handleUpload}
              disabled={uploading}
              className="hidden"
            />
          </label>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search files..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-card border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          
          <div className="flex gap-2 flex-wrap">
            {['all', 'image', 'audio', 'video', 'archive'].map((type) => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`px-4 py-3 rounded-xl font-medium transition-all ${
                  filterType === type
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card border border-border text-foreground hover:border-primary/50'
                }`}
              >
                {type === 'archive' ? 'ZIP/Archive' : type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Media Grid */}
        {filteredMedia.length === 0 ? (
          <div className="text-center py-16">
            <FolderOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
            <h3 className="text-xl font-bold text-foreground mb-2">No media found</h3>
            <p className="text-muted-foreground">
              {searchQuery ? 'Try a different search term' : 'Upload some files to get started'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            <AnimatePresence>
              {filteredMedia.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ delay: index * 0.03 }}
                  className="group relative bg-card border border-border/50 rounded-xl overflow-hidden hover:border-primary/50 transition-all"
                >
                  {/* Preview */}
                  <div 
                    className="aspect-square cursor-pointer"
                    onClick={() => setSelectedMedia(item)}
                  >
                    {(item.mime_type?.startsWith('image') || item.url?.match(/\.(jpg|jpeg|png|gif|webp)$/i)) ? (
                      <img
                        src={getFullUrl(item.url)}
                        alt={item.filename}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center bg-stone-800"><span class="text-xs text-muted-foreground">Image not found</span></div>';
                        }}
                      />
                    ) : item.cover_url ? (
                      // Audio with album cover
                      <div className="relative w-full h-full group/cover">
                        <img
                          src={getFullUrl(item.cover_url)}
                          alt={item.filename}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute bottom-2 right-2 p-1.5 bg-black/70 rounded-full">
                          <Music className="w-4 h-4 text-primary" />
                        </div>
                        {/* Play button overlay */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const audio = new Audio(getFullUrl(item.url));
                            audio.play();
                          }}
                          className="absolute inset-0 bg-black/40 opacity-0 group-hover/cover:opacity-100 flex items-center justify-center transition-opacity"
                        >
                          <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                            <svg className="w-6 h-6 text-black ml-1" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z"/>
                            </svg>
                          </div>
                        </button>
                        {/* Change cover button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSetCoverFor(item);
                          }}
                          className="absolute top-2 right-2 px-2 py-1 bg-black/70 text-white text-xs rounded opacity-0 group-hover/cover:opacity-100 transition-opacity"
                        >
                          Change Cover
                        </button>
                      </div>
                    ) : (
                      // Audio without cover - show placeholder with button to set cover
                      <div className="w-full h-full flex flex-col items-center justify-center bg-stone-800 gap-3 group/audio">
                        <Music className="w-10 h-10 text-primary" />
                        {/* Play button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const audio = new Audio(getFullUrl(item.url));
                            audio.play();
                          }}
                          className="w-10 h-10 bg-primary/80 hover:bg-primary rounded-full flex items-center justify-center transition-colors"
                        >
                          <svg className="w-5 h-5 text-black ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z"/>
                          </svg>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSetCoverFor(item);
                          }}
                          className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-medium rounded transition-colors"
                        >
                          + Add Cover
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-3">
                    <p className="text-sm font-medium text-foreground truncate" title={item.filename}>
                      {item.filename}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(item.size)}
                    </p>
                  </div>

                  {/* Actions Overlay */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-opacity flex items-center justify-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        copyUrl(item.url);
                      }}
                      className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                      title="Copy URL"
                    >
                      <Copy className="w-5 h-5 text-white" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirm(item);
                      }}
                      className="p-2 bg-red-500/80 hover:bg-red-500 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-5 h-5 text-white" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Media Preview Modal */}
        <AnimatePresence>
          {selectedMedia && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
              onClick={() => setSelectedMedia(null)}
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-card border border-border rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-4 border-b border-border flex items-center justify-between">
                  <h3 className="font-bold text-foreground truncate">{selectedMedia.filename}</h3>
                  <button
                    onClick={() => setSelectedMedia(null)}
                    className="p-2 hover:bg-background rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-foreground" />
                  </button>
                </div>
                
                <div className="p-4">
                  {selectedMedia.mime_type.startsWith('image') ? (
                    <img
                      src={getFullUrl(selectedMedia.url)}
                      alt={selectedMedia.filename}
                      className="max-w-full max-h-[60vh] mx-auto rounded-lg"
                    />
                  ) : selectedMedia.mime_type.startsWith('audio') ? (
                    <div className="p-8 text-center">
                      <Music className="w-24 h-24 text-primary mx-auto mb-4" />
                      <p className="text-foreground font-medium mb-4">{selectedMedia.filename}</p>
                      <audio controls className="w-full max-w-md mx-auto">
                        <source src={getFullUrl(selectedMedia.url)} type={selectedMedia.mime_type} />
                      </audio>
                    </div>
                  ) : (
                    <div className="p-8 text-center">
                      <FolderOpen className="w-24 h-24 text-muted-foreground mx-auto mb-4" />
                      <p className="text-foreground">{selectedMedia.filename}</p>
                    </div>
                  )}
                </div>

                <div className="p-4 border-t border-border">
                  <div className="flex items-center gap-2 bg-background rounded-lg p-2">
                    <input
                      type="text"
                      value={getFullUrl(selectedMedia.url)}
                      readOnly
                      className="flex-1 bg-transparent text-sm text-foreground focus:outline-none"
                    />
                    <button
                      onClick={() => copyUrl(selectedMedia.url)}
                      className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
                    >
                      Copy URL
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Delete Confirmation Modal */}
        <AnimatePresence>
          {deleteConfirm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
              onClick={() => setDeleteConfirm(null)}
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-card border border-border rounded-2xl p-6 max-w-md w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="text-xl font-bold text-foreground mb-2">Delete File?</h3>
                <p className="text-muted-foreground mb-6">
                  Are you sure you want to delete "{deleteConfirm.filename}"? This action cannot be undone.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setDeleteConfirm(null)}
                    className="flex-1 px-4 py-3 bg-background border border-border rounded-xl font-medium text-foreground hover:bg-card transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    data-testid="confirm-delete-btn"
                    onClick={() => handleDelete(deleteConfirm.id)}
                    className="flex-1 px-4 py-3 bg-red-500 hover:bg-red-600 rounded-xl font-medium text-white transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}

          {/* Set Cover Image Modal */}
          {setCoverFor && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
              onClick={() => setSetCoverFor(null)}
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-card border border-border rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-4 border-b border-border flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-foreground">Select Cover Image</h3>
                    <p className="text-sm text-muted-foreground">for "{setCoverFor.filename}"</p>
                  </div>
                  <button
                    onClick={() => setSetCoverFor(null)}
                    className="p-2 hover:bg-background rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-foreground" />
                  </button>
                </div>
                
                <div className="p-4 overflow-y-auto max-h-[60vh]">
                  <div className="grid grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                    {media.filter(m => m.mime_type?.startsWith('image')).map((img) => (
                      <button
                        key={img.id}
                        onClick={() => handleSetCover(setCoverFor, img.url)}
                        className="aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-primary transition-all"
                      >
                        <img
                          src={getFullUrl(img.url)}
                          alt={img.filename}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="p-4 border-t border-border flex justify-between">
                  <button
                    onClick={() => handleSetCover(setCoverFor, null)}
                    className="px-4 py-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                  >
                    Remove Cover
                  </button>
                  <button
                    onClick={() => setSetCoverFor(null)}
                    className="px-4 py-2 bg-background border border-border rounded-lg hover:bg-card transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
